import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, LinearProgress, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { DropzoneArea } from 'material-ui-dropzone';
import { read, utils, writeFileXLSX } from 'xlsx';
import validator from 'validator';
import './Import.css';
import IssuesTab from './IssuesTab';
import FileSummary from './FileSummary';
import { importEmployeeByExcel } from 'src/services/employeeService';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';

const ImportModalForm = ({ toggle, getData }) => {
  const dispatch = useDispatch();

  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState('');
  const [fileTemplateStatus, setFileTemplateStatus] = useState(false);
  const [tableValues, setTableValues] = useState<any>([]);
  const [isDisabled, setIsDisabled] = useState(true);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [headers] = useState([
    'Employee Name',
    'NIC',
    'Gender',
    'Marital Status',
    'Date Of Birth',
    'Country (For Country Code)',
    'Phone',
    'Country (For Country Code)',
    'Emergency Phone',
    'Emergency Contact Name',
    'Emergency Contact Relationship',
    'Address',
    'Current Address',
    'Skype ID',
    'Official Email Address',
    'Designation',
    'Employee ID',
    'Tier',
    'Joined Date',
    'Employment Type',
    'Employment Type Starting Date',
    'Employment Type Ending Date',
    'Primary Reporting Manager',
    'User Role'
  ]);

  const [requiredFields] = useState([
    'Employee Name',
    'NIC',
    'Gender',
    'Marital Status',
    'Date Of Birth',
    'Country (For Country Code)',
    'Phone',
    'Country (For Country Code)',
    'Emergency Phone',
    'Emergency Contact Name',
    'Emergency Contact Relationship',
    'Address',
    'Current Address',
    'Skype ID',
    'Official Email Address',
    'Designation',
    'Employee ID',
    'Tier',
    'Joined Date',
    'Employment Type',
    'Employment Type Starting Date',
    'Primary Reporting Manager',
    'User Role'
  ]);

  const [attachment, setAttachment] = useState<any>({});

  const [timerStarted, setTimerStarted] = useState(false);
  const [progress, setProgress] = useState(0);

  const [successRecords, setSuccessRecords] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [invalidValues, setInvalidValues] = useState([]);
  const [invalidRows, setInvalidRows] = useState(0);

  const [openIssues, setOpenIssues] = useState(false);

  const onImport = () => {
    if (attachment) {
      importEmployeeByExcel(attachment.file).then((res) => {
        const data = res.data;

        setSuccessRecords(data?.successRecords);
        setTotalRecords(data?.totalRecords);
        if (Object.keys(data?.errors).length > 0) {
          const entries = Object.entries(data.errors);
          const allErrors = entries.map((entry) => {
            return {
              error: entry[1],
              row: Number(entry[0])  - 1
            };
          });

          toggleIssues();
          setInvalidValues([...allErrors]);
        } else{
          toggle();
          getData();
        }
      })
      .catch((err) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage(err?.response?.data ? err?.response?.data : 'Unable to import employee');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
    }
  };

  const handleFileChange = (files) => {
    setTableValues([]);
    setFileTemplateStatus(false);
    setProgress(0);
    setIsDisabled(true);
    if (files[0]) {
      setAttachment({ file: files[0] });
      setFileName(files[0].name);
      const reader = new FileReader();
      if (reader.readAsBinaryString) {
        reader.onload = (e) => {
          const workbook = read(reader.result, { type: 'binary' });
          setProgress(10);
          setIsDisabled(true);
          const headerSheets = workbook.Sheets[workbook.SheetNames[0]];
          const excelHeaders: any = utils.sheet_to_json(headerSheets, {
            header: 1
          });

          const firstSheet = workbook.SheetNames[0];
          const excelRows = utils.sheet_to_json(workbook.Sheets[firstSheet]);

          if (
            !excelHeaders[0]?.every((value, index) => value === headers[index])
          ) {
            setFileTemplateStatus(false);
            setErrors('File template is invalid');
          } else if(excelRows.length === 0){
            setFileTemplateStatus(false);
            setErrors('Values are empty, please fill and import it again.');
          } else {
            setTableValues(excelRows);
            setFileTemplateStatus(true);
          }
        };
        setProgress(20);
        setIsDisabled(true);
        reader.readAsBinaryString(files[0]);
      }
    }
  };

  useEffect(() => {
    if (fileTemplateStatus) {
      setErrors('');
    }
  }, [fileTemplateStatus]);

  useEffect(() => {
    setProgress(0);
    setIsDisabled(true);
  }, [fileName]);

  const toggleIssues = () => {
    setOpenIssues(!openIssues);
  };

  useEffect(() => {
    setSuccessRecords(tableValues.length - invalidRows);
    setTotalRecords(tableValues.length);
  }, [tableValues, invalidRows]);

  useEffect(() => {
    if (tableValues.length > 0 && fileTemplateStatus) {
      let emptyRowArr = [];
      let invalidEmails = [];
      let invalidRow = 0;
      
      tableValues.forEach((tab) => {
        const rowNum = tab.__rowNum__;
        setProgress(40);
        setIsDisabled(true);
        const entries = Object.entries(tab);
        const emptyFields = requiredFields.filter((head) => {
          if (!entries.some((val) => val[0] === head)) return head;
        });

        const emptyFieldsRows = emptyFields.map((field) => {
          return {
            error: `${field} is Required`,
            row: rowNum
          };
        });

        if (emptyFieldsRows.length > 0) {
          emptyRowArr = [...emptyRowArr, ...emptyFieldsRows];
        }
        setTimeout(() => {
          setProgress(70);
        }, 500);
        setIsDisabled(true);

        const emailSet = entries.filter(
          (entry) => entry[0] === 'Official Email Address'
        );
        if (emailSet[0]?.length > 0) {
          if (!validator.isEmail(emailSet[0][1])) {
            invalidEmails.push({
              error: 'Email is invalid',
              row: rowNum
            });
          }
        }

        if (emptyFieldsRows.length > 0 || invalidEmails.length > 0) {
          invalidRow = invalidRow + 1;
        }
      });
      setTimeout(() => {
        setProgress(100);
      }, 1000);
      setIsDisabled(false);
      setInvalidValues([...emptyRowArr, ...invalidEmails]);
      setInvalidRows(invalidRow);
    }
  }, [tableValues, requiredFields, fileTemplateStatus]);

  return (
    <>
      <Grid sx={{ px: 2, mt: '0.5rem' }} container spacing={2}>
        <Grid item xs={12}>
          <Typography
            style={{
              color: '#223354',
              fontWeight: '700',
              fontSize: '15px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
            sx={{ pb: 1 }}
            variant="h4"
          >
            Import Employees
          </Typography>
        </Grid>
        {!fileTemplateStatus && (
          <Grid display="flex" alignItems="center" item xs={12}>
            <Grid item xs={9}>
              <Typography sx={{ color: 'red' }} variant="body2">
                Please make sure to use the given template
              </Typography>
            </Grid>
            <Grid display="flex" justifyContent="end" item xs={3}>
              <Button
                id='btn-empDetails-download'
                sx={{
                  mt: { xs: 2, md: 0 },
                  fontSize: '11px',
                  fontWeight: 'normal'
                }}
                variant="outlined"
                startIcon={<GetAppIcon fontSize="small" />}
                href={process.env.PUBLIC_URL + '/templates/Template.xlsx'}
                download="template"
              >
                Download Template
              </Button>
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <DropzoneArea
            acceptedFiles={['.xlsx', '.xls']}
            onChange={(e) => handleFileChange(e)}
            showPreviews={false}
            showPreviewsInDropzone={false}
            filesLimit={1}
            showAlerts={false}
          />
          {errors && (
            <Typography sx={{ color: 'red' }} variant="body2">
              {errors}
            </Typography>
          )}
        </Grid>
        {fileTemplateStatus && (
          <Grid item xs={12}>
            {/* <Box
              className="loader-container"
              display="flex"
              alignItems="center"
              sx={{ width: '100%' }}
            > */}
              <img
                src={process.env.PUBLIC_URL + '/img/excelsheet.jpg'}
                alt=""
                style={{
                  width: '48px',
                  height: '48px'
                }}
              />
              <Box sx={{ width: '100%' }}>
                <Grid item display="flex" justifyContent="between">
                  <p style={{ marginBottom: 0 }}>{fileName}</p>
                  <p style={{ marginBottom: 0, marginLeft: 'auto' }}>
                    {progress}
                  </p>
                </Grid>

                <LinearProgress variant="determinate" value={progress} />
              </Box>
            {/* </Box> */}
          </Grid>
        )}
        {tableValues?.length > 0 && (
          <FileSummary
            tableValues={tableValues}
            successRecords={successRecords}
            totalRecords={totalRecords}
            toggleIssues={toggleIssues}
            invalidRows={invalidRows}
            errorsCount={totalRecords - successRecords || 0}
          />
        )}
        <Grid container spacing={2}>
          <Grid item xs={6}></Grid>
          <Grid
            style={{
              marginTop: '3rem',
              marginBottom: '1rem'
            }}
            item
            sx={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
            xs={6}
          >
            <Button
              id="btn-empDetails-toggle"
              style={{
                minWidth: '64px',
                padding: '6px 16px',
                backgroundColor: '#5569ff',
                paddingLeft: '16px',
                paddingRight: '16px',
                color: '#FFFFFF',
                borderRadius: '6px',
                width: '30%'
              }}
              variant="outlined"
              onClick={() => toggle()}
            >
              Close
            </Button>
            <Button
              id="btn-empDetails-onImport"
              style={{
                minWidth: '64px',
                padding: '6px 16px',
                backgroundColor: isDisabled ? '#eee' : '#5569ff',
                paddingLeft: '16px',
                paddingRight: '16px',
                color: isDisabled ? 'rgba(34, 51, 84, 0.5)' : '#FFFFFF',
                borderRadius: '6px',
                marginLeft: '1rem',
                width: '30%'
              }}
              disabled={isDisabled}
              variant="contained"
              onClick={() => onImport()}
            >
              Import
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <IssuesTab
        toggle={toggleIssues}
        open={openIssues}
        invalidValues={invalidValues}
      />
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </>
  );
};

export default ImportModalForm;
